/**
 * @fileoverview Dados Master (Imutáveis) da DRE - Versão Original do Excel
 * @module lib/dre/masterData
 * 
 * Este módulo contém os dados históricos ORIGINAIS extraídos do Excel,
 * convertidos para o formato DRE. Estes dados servem como baseline
 * e são armazenados na tabela `dre_master` (read-only).
 * 
 * Fonte: Valuation_Setima_555_Completo.xlsx
 * Tabs: 03_DRE_Setima, 04_DRE_555_Studios
 * 
 * @see DEFAULT_HISTORICAL_DATA em src/core/ValuationEngine.ts
 */

import type { DREData, DREYear } from '../../types/dre';

/**
 * Premissas de estrutura de DRE Sétima (baseadas no HistoricoParser)
 * Usadas para reconstruir DRE completa a partir de Receita Bruta e EBITDA
 */
const PREMISSAS_SETIMA = {
  impostoVendas: 0.08,      // 8%
  margemCustos: 0.35,       // Custos diretos ~35%
  depreciacao: 0.03,        // 3%
  impostoLucro: 0.18,       // 18%
};

/**
 * Reconstrói DRE completa a partir de Receita Bruta e EBITDA conhecidos
 * Mesma lógica do HistoricoParser para consistência
 */
function reconstruirDRESétima(
  year: number,
  receitaBruta: number,
  ebitda: number,
  dadosReais?: Partial<DREYear>
): DREYear {
  // Se temos dados reais completos (2023), usar eles
  if (dadosReais && dadosReais.lucroLiquido !== undefined) {
    const receitaLiquida = dadosReais.receitaLiquida || receitaBruta * (1 - PREMISSAS_SETIMA.impostoVendas);
    
    return {
      year,
      receitaBruta,
      impostosVendas: dadosReais.impostosVendas || receitaBruta * PREMISSAS_SETIMA.impostoVendas,
      receitaLiquida,
      custosDirectos: dadosReais.custosDirectos || 0,
      lucroBruto: dadosReais.lucroBruto || 0,
      despesasOperacionais: dadosReais.despesasOperacionais || 0,
      ebitda,
      depreciacao: dadosReais.depreciacao || receitaBruta * PREMISSAS_SETIMA.depreciacao,
      ebit: dadosReais.ebit || (ebitda - (dadosReais.depreciacao || receitaBruta * PREMISSAS_SETIMA.depreciacao)),
      impostosLucro: dadosReais.impostosLucro || 0,
      lucroLiquido: dadosReais.lucroLiquido,
      margemBruta: dadosReais.lucroBruto && receitaLiquida > 0 ? (dadosReais.lucroBruto / receitaLiquida) * 100 : 0,
      margemEbitda: receitaLiquida > 0 ? (ebitda / receitaLiquida) * 100 : 0,
      margemEbit: dadosReais.ebit && receitaLiquida > 0 ? (dadosReais.ebit / receitaLiquida) * 100 : 0,
      margemLiquida: dadosReais.lucroLiquido && receitaLiquida > 0 ? (dadosReais.lucroLiquido / receitaLiquida) * 100 : 0,
    };
  }

  // Caso contrário, reconstruir usando premissas
  const impostosVendas = receitaBruta * PREMISSAS_SETIMA.impostoVendas;
  const receitaLiquida = receitaBruta - impostosVendas;
  
  // Trabalhar backward do EBITDA
  const depreciacao = receitaBruta * PREMISSAS_SETIMA.depreciacao;
  const ebit = ebitda - depreciacao;
  const impostosLucro = ebit > 0 ? ebit * PREMISSAS_SETIMA.impostoLucro : 0;
  const lucroLiquido = ebit - impostosLucro;
  
  // Trabalhar forward para completar
  const custosDirectos = receitaLiquida * PREMISSAS_SETIMA.margemCustos;
  const lucroBruto = receitaLiquida - custosDirectos;
  const despesasOperacionais = lucroBruto - ebitda;
  
  return {
    year,
    receitaBruta,
    impostosVendas,
    receitaLiquida,
    custosDirectos,
    lucroBruto,
    despesasOperacionais,
    ebitda,
    depreciacao,
    ebit,
    impostosLucro,
    lucroLiquido,
    margemBruta: receitaLiquida > 0 ? (lucroBruto / receitaLiquida) * 100 : 0,
    margemEbitda: receitaLiquida > 0 ? (ebitda / receitaLiquida) * 100 : 0,
    margemEbit: receitaLiquida > 0 ? (ebit / receitaLiquida) * 100 : 0,
    margemLiquida: receitaLiquida > 0 ? (lucroLiquido / receitaLiquida) * 100 : 0,
  };
}

/**
 * Dados históricos da SÉTIMA (2020-2024)
 * Reconstruídos com premissas consistentes do HistoricoParser
 */
export const MASTER_SETIMA_YEARS: DREYear[] = [
  reconstruirDRESétima(2020, 6560028.82, 1515712.34),
  reconstruirDRESétima(2021, 8164232.07, 2289036.86),
  reconstruirDRESétima(2022, 10238189.48, 3288778.71),
  // 2023 - Dados reais completos do Excel (com PREJUÍZO)
  reconstruirDRESétima(2023, 7048065.74, 79502.46, {
    receitaLiquida: 6544261.93,
    custosDirectos: 3814367.53,
    lucroBruto: 2729894.40,
    despesasOperacionais: 2650391.94,
    impostosLucro: 606227.34,
    lucroLiquido: -535145.72, // PREJUÍZO!
  }),
  reconstruirDRESétima(2024, 8548359.60, 954086.99),
];

/**
 * Premissas de estrutura de DRE 555 Studios (baseadas no HistoricoParser)
 */
const PREMISSAS_555 = {
  impostoVendas: 0.06,      // 6%
  margemCustos: 0.35,       // 35%
  depreciacao: 0.02,        // 2%
  impostoLucro: 0.18,       // 18%
};

/**
 * Reconstrói DRE completa para 555 Studios
 */
function reconstruirDRE555(
  year: number,
  receitaBruta: number,
  ebitda: number
): DREYear {
  const impostosVendas = receitaBruta * PREMISSAS_555.impostoVendas;
  const receitaLiquida = receitaBruta - impostosVendas;
  
  // Trabalhar backward do EBITDA
  const depreciacao = receitaBruta * PREMISSAS_555.depreciacao;
  const ebit = ebitda - depreciacao;
  const impostosLucro = ebit > 0 ? ebit * PREMISSAS_555.impostoLucro : 0;
  const lucroLiquido = ebit - impostosLucro;
  
  // Trabalhar forward para completar
  const custosDirectos = receitaLiquida * PREMISSAS_555.margemCustos;
  const lucroBruto = receitaLiquida - custosDirectos;
  const despesasOperacionais = lucroBruto - ebitda;
  
  return {
    year,
    receitaBruta,
    impostosVendas,
    receitaLiquida,
    custosDirectos,
    lucroBruto,
    despesasOperacionais,
    ebitda,
    depreciacao,
    ebit,
    impostosLucro,
    lucroLiquido,
    margemBruta: receitaLiquida > 0 ? (lucroBruto / receitaLiquida) * 100 : 0,
    margemEbitda: receitaLiquida > 0 ? (ebitda / receitaLiquida) * 100 : 0,
    margemEbit: receitaLiquida > 0 ? (ebit / receitaLiquida) * 100 : 0,
    margemLiquida: receitaLiquida > 0 ? (lucroLiquido / receitaLiquida) * 100 : 0,
  };
}

/**
 * Dados históricos da 555 STUDIOS (2023-2024)
 * Reconstruídos com premissas consistentes do HistoricoParser
 */
export const MASTER_555_YEARS: DREYear[] = [
  reconstruirDRE555(2023, 1132800.00, 465742.18),
  reconstruirDRE555(2024, 3176911.50, 1143645.33),
];

/**
 * Dados Master completos (Sétima + 555 Studios)
 * Usados como baseline para todas as versões editáveis
 */
export const MASTER_DRE_DATA: Omit<DREData, 'id' | 'userId' | 'createdAt' | 'updatedAt'> = {
  setima: {
    companyId: 'setima',
    companyName: 'Sétima',
    years: MASTER_SETIMA_YEARS,
  },
  studio555: {
    companyId: '555',
    companyName: '555 Studios',
    years: MASTER_555_YEARS,
  },
};

/**
 * Metadados da versão master
 */
export const MASTER_METADATA = {
  source: 'Dados Históricos Originais - Modelo Excel',
  sourceFile: 'Valuation_Setima_555_Completo.xlsx',
  sourceTabs: '03_DRE_Setima, 04_DRE_555_Studios',
  description: 'Versão imutável dos dados extraídos do Excel. Serve como baseline para comparação com versões editáveis.',
  isMaster: true,
  isReadOnly: true,
} as const;

/**
 * Validação: garantir que dados master estão completos
 */
export function validateMasterData(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Validar Sétima
  if (MASTER_SETIMA_YEARS.length !== 5) {
    errors.push(`Sétima deve ter 5 anos (2020-2024), encontrado: ${MASTER_SETIMA_YEARS.length}`);
  }

  const setimaYears = MASTER_SETIMA_YEARS.map(y => y.year).sort();
  const expectedSetimaYears = [2020, 2021, 2022, 2023, 2024];
  if (JSON.stringify(setimaYears) !== JSON.stringify(expectedSetimaYears)) {
    errors.push(`Sétima anos incorretos. Esperado: ${expectedSetimaYears}, Encontrado: ${setimaYears}`);
  }

  // Validar 555 Studios
  if (MASTER_555_YEARS.length !== 2) {
    errors.push(`555 Studios deve ter 2 anos (2023-2024), encontrado: ${MASTER_555_YEARS.length}`);
  }

  const studio555Years = MASTER_555_YEARS.map(y => y.year).sort();
  const expectedStudio555Years = [2023, 2024];
  if (JSON.stringify(studio555Years) !== JSON.stringify(expectedStudio555Years)) {
    errors.push(`555 Studios anos incorretos. Esperado: ${expectedStudio555Years}, Encontrado: ${studio555Years}`);
  }

  // Validar valores não-negativos
  [...MASTER_SETIMA_YEARS, ...MASTER_555_YEARS].forEach(year => {
    if (year.receitaBruta < 0 || year.ebitda < 0) {
      errors.push(`Ano ${year.year}: valores negativos encontrados`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// Auto-validação em desenvolvimento
if (import.meta.env.DEV) {
  const validation = validateMasterData();
  if (!validation.isValid) {
    console.error('❌ MASTER_DRE_DATA inválido:', validation.errors);
  } else {
    console.log('✅ MASTER_DRE_DATA validado com sucesso');
  }
}
